import { useEffect, useMemo, useState } from "react";

import type SelectionDropdown from "../models/selection-dropdown/SelectionDropdown";
import ManualSelectionItem from "../models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../models/selection-dropdown-item/types";

type Deps<Item extends SelectionDropdownItem> = {
    limit?: number;
    numberOfResults: number;
    numberOfSelectedItems: number;
    mapLimitedListOfItems?: ((listOfItems: readonly number[]) => readonly Item[]) | undefined;
    onSelectionChange(item: SelectionDropdownItem): void;
};

type HookValue<Item extends SelectionDropdownItem> = {
    activeSelectionItem: Item | SelectionDropdownItem;
    dropdownItems: readonly Item[];
    isSelectionEmpty: boolean;
    manualSelectionItem: ManualSelectionItem;
    setActiveSelectionItem: (item: Item) => void;
};

/** @deprecated Use `useActionPanelState` from `pages/sales-intelligence/common-components/table-actions-panel/hooks/useActionsPanelState.ts` */
function useActionsPanelState<Item extends SelectionDropdownItem = SelectionDropdownItem>(
    selectionDropdown: Readonly<SelectionDropdown>,
    deps: Deps<Item>,
): HookValue<Item> {
    const { limit, numberOfResults, numberOfSelectedItems, onSelectionChange, mapLimitedListOfItems } = deps;

    const manualSelectionItem = new ManualSelectionItem(
        numberOfSelectedItems,
        undefined,
        selectionDropdown.i18nKeyModifier,
    );

    const dropdownItems = useMemo(() => {
        return selectionDropdown.getItems(numberOfResults, limit, mapLimitedListOfItems);
    }, [numberOfResults, limit]);

    const [activeSelectionItem, setActiveSelectionItem] = useState<Item | SelectionDropdownItem>(
        selectionDropdown.getInitiallySelectedItem(dropdownItems),
    );

    const isSelectionEmpty = activeSelectionItem.id === ManualSelectionItem.ID && numberOfSelectedItems === 0;

    useEffect(() => {
        const expectedSelectedItem = selectionDropdown.getInitiallySelectedItem(dropdownItems);

        if (activeSelectionItem.id !== expectedSelectedItem.id) {
            setActiveSelectionItem(expectedSelectedItem);
        }
    }, [dropdownItems]);

    useEffect(() => {
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            setActiveSelectionItem(manualSelectionItem);
        }
    }, [numberOfSelectedItems]);

    useEffect(() => {
        onSelectionChange(activeSelectionItem);
    }, [activeSelectionItem.id]);

    return {
        activeSelectionItem,
        dropdownItems,
        isSelectionEmpty,
        manualSelectionItem,
        setActiveSelectionItem,
    };
}

export default useActionsPanelState;
